<template>
    <div>
        <div
            ref="generalHolder"
            class="content-widget"
            :style="{'background-color': 'background-color' in defaultStyles ? defaultStyles['background-color'] : null }"
        />
    </div>
</template>

<script>
    import _ from 'lodash';
    import { mapGetters } from 'vuex';
    import Http from '@/api/data';
    import { EventBus } from '@/event-bus';

    import { oneForAllProjects } from '@/private/oneForAllProjects';

    export default {
        name: 'OneForAllWidget',
        data: () => ({
            widget: null,
            firstScale: null,
            bus: null,
            floor: 0,
            floorIndex: 0,
            views: null,
            apiData: null,
            currentLink: null,
            currentLevel: 0,
            currentResolution: '720p',
            currentIndex: 0,
            defaultWidget: 'digital-model',
            currentView: 'digital-model',
            isPlaying: false,
            isFirstLoad: true
        }),
        computed: {
            ...mapGetters([
                'protocol'
            ]),
            oneForAllProjectParams() {
                let ressourceId = this.$route.query.id ? this.$route.query.id : null;
                // Retrieve corresponding ID from backoffice
                return ressourceId ? oneForAllProjects[ressourceId] : null;
            },
            defaultStyles() {
                return 'styles' in this.oneForAllProjectParams ? this.oneForAllProjectParams.styles : null;
            },
            startView() {
                return this.$route.query.startView ? this.$route.query.startView : null;
            }
        },
        created() {
            let vm = this;
            this.bus = (window.iframeBus = new BI.api.Bus());

            // Event to load digital-model
            this.bus.subscribe('loadView', widget => {
                if (widget === vm.currentView)
                    return;
                vm.currentView = widget;
                Object.keys(this.views).forEach(view => {
                    this.views[view].active = this.views[view].id === vm.currentView;
                });
                vm.getWidgetData();
            });

            // Event to update view properties (zoom, view angle, digital-model level's)
            this.bus.subscribe('updateProperty', property => {
                switch (property.name) {
                    case 'zoom':
                        this.widget.setZoom(property.value);
                        break;
                    case 'index':
                        if (this.isPlaying) {
                            this.widget.rotateX(property.value);
                        } else {
                            this.apiData.startIndex = property.value;
                        }
                        break;
                    case 'level':
                        this.updateLevelData(property.value);
                        if (this.currentView !== 'floors')
                            return;
                        if (this.isPlaying) {
                            this.widget.rotateY(property.value);
                        }
                        break;
                    case 'lock':
                        this.widget.setLockStatus(property.value);
                        break;
                }
            });

            // Event to update plot properties (visibility, colors)
            this.bus.subscribe('updateZone', data => {
                let id = data['id'];
                if (typeof data['visible'] !== 'undefined') {
                    this.apiData.levels.forEach((level, index) => {
                        level.hotspots.forEach(hotspot => {
                            if (hotspot['name'] === id) {
                                hotspot['visible'] = !!data['visible'];
                            }
                        });
                    });
                }

                if (typeof data['color'] !== 'undefined') {
                    this.apiData.levels.forEach(function (level, index) {
                        level.hotspots.forEach(function (hotspot) {
                            if (hotspot['name'] === id) {
                                hotspot['color'] = data['color'];
                            }
                        });
                    });
                }

                this.apiData.frames = this.apiData.levels[this.currentLevel].frames;
                this.apiData.hotspots = this.apiData.levels[this.currentLevel].hotspots;

                this.widget.loadLevel(this.apiData);
            });

            // Event to update the digital-model widget
            this.bus.subscribe('play', () => {
                vm.play();
            });

            // Init widget
            this.getWidgetData();
        },
        methods: {
            callbacks() {
                let vm = this;
                return {
                    callbacks: {
                        // Callback triggered on updating level
                        levelCallback: function (level) {
                            vm.updateLevelData(level);
                            vm.widget.loadLevel(vm.apiData);
                            vm.bus.publish('onLevelChange', level);
                        },
                        // Callback triggered after click event
                        clickCallback: function (zone) {
                            vm.bus.publish('onZoneClick', zone.name);
                        },
                        // Callback triggered on updating angle view
                        indexCallback: function(currentAngle, maxViews) {
                            if (vm.currentIndex !== currentAngle) {
                                vm.currentIndex = currentAngle;
                                vm.bus.publish('onIndexChange', currentAngle);
                            }
                        },
                        // Callback triggered on updating zoom
                        zoomCallback: function(zoom) {
                            vm.bus.publish('onZoomChange', zoom);
                        }
                    },
                };
            },
            getUrlLink() {
                let url = `${this.protocol}${process.env.API_LIST.FO}`;
                switch (this.currentView) {
                    case 'digital-model':
                        url += '/request-data/digital-model';
                        break;

                    case 'floors':
                        url += '/request-data/cutaway-view';
                        break;

                    default:
                        url += `/request-data/digital-model/${this.currentView}`;
                        break;
                }
                return `${url}?id=${this.oneForAllProjectParams['id']}&key=${this.oneForAllProjectParams['key']}`;
            },
            // Return all image from all floors (Cutaway widget case)
            getImageFromLevels(levels) {
                var images = [];
                if (levels !== null) {
                    levels.forEach((level, index) => {
                        if ('images' in level) {
                            images[index] = level.images.length ? level.images[0] : null;
                        }
                    });
                }
                return images;
            },
            getWidgetData() {
                this.currentLink = this.getUrlLink();
                Http.getData(this.currentLink).then(payload => {
                    if (payload) {
                        this.apiData = Object.assign(payload, this.callbacks());
                        // Concat "widget type" with "digital-model ID" in order to be able to persist more than one digital-model for each type
                        let digitalModelUrl = new URL(this.currentLink);
                        let digitalModelParam = new URLSearchParams(digitalModelUrl.search);
                        this.apiData.widgetType = `${this.currentView}-${digitalModelParam.get('id')}`;
                        this.apiData.currentResolution = this.currentResolution;
                        this.apiData.startLevel = this.currentView === 'floors' ? this.currentLevel : 0;
                        this.apiData.lockPreloadFrames = true;
                        if (this.currentView === 'floors') {
                            let levelData = payload.levels && payload.levels[this.currentLevel] ? payload.levels[this.currentLevel] : null;
                            this.apiData.startLevel = this.currentLevel;
                            this.apiData.images = this.getImageFromLevels(payload.levels);
                            this.apiData.frames = levelData.frames || [];
                            this.apiData.hotspots = levelData.hotspots || [];
                        } else {
                            this.apiData.startLevel = 0;
                            this.apiData.frames = [];
                            this.apiData.hotspots = [];
                        }

                        // Set available widget list
                        if (!this.views) {
                            this.views = {};
                            this.apiData.levels.forEach(level => {
                                let widgetId = level.id;
                                if (level.id === 'cutaway') {
                                    widgetId = 'floors';
                                }
                                this.views[widgetId] = {
                                    'id': widgetId,
                                    'active': level.active
                                };
                            });
                        }

                        if (this.startView && this.isFirstLoad) {
                            this.isFirstLoad = false;
                            this.currentView = this.startView;
                            this.getWidgetData();
                        } else {
                            this.sendData();
                        }
                    }
                })
                .catch((error) => {
                    EventBus.$emit('setErrorState', true, error);
                });
            },
            sendData() {
                this.isPlaying = false;
                let mapLots = {};
                if (this.apiData.levels) {
                    this.apiData.levels.forEach((level, index) => {
                        if (level.hotspots) {
                            level.hotspots.forEach(hotspot => {
                                mapLots[hotspot.name] = mapLots[hotspot.name] || {
                                    'id': hotspot.name,
                                    'visible': hotspot.visible,
                                    'color': hotspot.color || "7, 153, 6",
                                    'levels': [],
                                },
                                mapLots[hotspot.name]['levels'].push(index);
                            });
                        }
                    });
                }

                Object.keys(this.views).forEach(view => {
                    this.views[view].active = this.views[view].id === this.currentView;
                });

                this.currentIndex = this.widget ? this.widget['canvas3D']['currentAngle'] : this.apiData.startIndex;
                this.bus.publish('onReady', {
                    'views': this.views,
                    'lots': mapLots,
                    'index': this.currentIndex,
                    'level': this.widget ? this.widget['canvas3D']['currentLevel'] : this.apiData.startLevel,
                    'previewImage': Array.isArray(this.apiData.images) && this.apiData.images[this.apiData.startLevel] ? this.apiData.images[this.apiData.startLevel][this.currentIndex] : null
                });
            },
            updateLevelData(level) {
                this.currentLevel = level;
                this.apiData.startLevel = level;
                this.apiData.frames = this.apiData.levels[level].frames;
                this.apiData.hotspots = this.apiData.levels[level].hotspots;
            },
            play() {
                if(this.isPlaying)
                    return;

                if (!this.widget) {
                    this.widget = new Habiteo.DigitalModelWidget(this.$refs.generalHolder, this.apiData);
                } else {
                    this.widget.loadData(this.apiData);
                }
                this.isPlaying = true;
            }
        }
    };
</script>


// WEBPACK FOOTER //
// src/views/OneForAllWidget.vue